import type { UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { createReactive } from "@supergrain/kernel"
import type { Editor } from "@tiptap/core"
import type { LessonModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"

/**
 * The drag item's type tag — filtering only. The real payload rides the module-scoped
 * {@link session}, not the `DataTransfer` (native DnD hides data until drop, so a
 * serialized payload would be unreadable during hover). An external drop carrying this
 * type is ignored because the session is `idle`.
 */
export { LESSON_DRAG_TYPE } from "@shared/unified-tiptap/util/drag-and-drop"

/**
 * Everything a drop needs about the dragged lesson — live documents, not ids, so the drop
 * handler never re-looks-them-up.
 */
export interface LessonDragSource {
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  modelsNeededForFirstEdit: LessonModelsNeededForFirstEdit
  getEditor: () => Editor | null
}

/** The slot a lesson can be dropped onto — a course-date cell in the planner. */
export interface LessonDropTarget {
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  courseDate: CourseDateCombined
}

/** No optional bag: a drop provably has a complete source, or there is no drag. */
export type LessonDragSession = { kind: "idle" } | { kind: "dragging"; source: LessonDragSource }

/**
 * The single source of truth for the in-flight lesson drag. Module-scoped because the
 * lesson islands are separate React roots in one Vite bundle — module scope is the only
 * thing that crosses roots (React context cannot). supergrain `createReactive`, never
 * `useSyncExternalStore` (precedent: surface-arbiter.ts).
 */
const state = createReactive<{ current: LessonDragSession }>({ current: { kind: "idle" } })

/** Read the current drag session (reactive: subscribes the reading `tracked` component). */
export function session(): LessonDragSession {
  return state.current
}

/** Begin a drag from `source`. Called from the drag source's `onDragStart`. */
export function startDrag(source: LessonDragSource): void {
  state.current = { kind: "dragging", source }
  clearPostDropAnchor()
}

/** End the drag (drop or cancel). Called from the drag source's `onDragEnd`. */
export function endDrag(): void {
  state.current = { kind: "idle" }
}

/**
 * The slot (course + date) that was dropped onto, and the viewport line its top sat on at the
 * moment of the drop — the place the user aimed at, and so the place it has to stay while every
 * collapsed lesson expands around it.
 */
export interface PostDropAnchor {
  courseId: string
  dateString: string
  /** `getBoundingClientRect().top` of the target slot, measured during the drop. */
  viewportTop: number
}

const anchorState = createReactive<{ current: PostDropAnchor | null }>({ current: null })

/** Read the pending post-drop anchor (reactive). Consumed by the target slot's chrome. */
export function pendingPostDropAnchor(): PostDropAnchor | null {
  return anchorState.current
}

/** Ask the dropped-on slot to hold the line it was dropped on while the planner reflows. */
export function anchorAfterDrop(anchor: PostDropAnchor): void {
  anchorState.current = anchor
}

export function clearPostDropAnchor(): void {
  anchorState.current = null
}
